import { action, observable } from "mobx";

type DisposerFunction = () => void;

/** clone object with proxy, removing proxies */
export function cloneObjectWithProxy(obj: object): object {
	if (!obj) {
		return obj;
	}
	const jsonString = JSON.stringify(obj);
	return jsonString ? JSON.parse(jsonString) : obj;
}

/** Inherit from class to be able to track and cleanup reactions */
export class ReactionStore<E extends Error = Error> {
	#childStores: ReactionStore[]  = [];
	disposers: DisposerFunction[] = [];
	@observable.ref _error: E = null;

	/** exception if any, supposed to used with react-error-boundary */
	get error(): E {
		return this._error;
	}

	/** set exception */
	@action setError = (error: E): void => {
		this._error = error;
	};

	trackChildStore = (store: ReactionStore): void => {
		if (!store) return;

		// prevents infinite loop from 2-way store relationship
		const isCircularReference = store.#childStores.includes(this);
		if (isCircularReference) {
			throw new Error(`Circular reference for ${store} to ${this.constructor} - reaction cleanup will not work as expected`);
		}

		this.#childStores.push(store);
	};

	cleanupReactions = (): void => {
		for (const disposer of this.disposers) {
			disposer();
		}

		for (const store of this.#childStores) {
			store.cleanupReactions();
		}
	};

	/** merge the disposer from other stores so that all the reactions can be cleaned up */
	mergeDisposers(store: ReactionStore): void {
		this.disposers = this.disposers.concat(store?.disposers || []);
	}

	trackReaction = (disposer: DisposerFunction): void => {
		this.disposers.push(disposer);
	};
}